import { HeatPoint } from '../../model/heat-point/heat-point';
import { AntennaList } from '../../model/antenna/antenna-list';
import { AntennaModelEnum } from '../../model/enum/antenna-model.enum';
import { TypeEnum } from '../../model/enum/type.enum';
import { Obstacle as ObstacleMaterial } from '../../model/obstacle/obstacle';
import {
  AccessPoint,
  DEFAULT_SYNTHETIC_GRID_CONFIG,
  Obstacle,
  SyntheticGridConfig
} from '../../services/commons/synthetic-grid.service';

export type WorkspaceMode = 'edit' | 'analyze';
export type ScenarioStateTone = 'neutral' | 'warning' | 'success' | 'active';

export interface ScenarioStateDescriptor {
  labelKey: string;
  tone: ScenarioStateTone;
}

export interface HeatmapAnalysisSummary {
  averageSignal: number | null;
  bestSignal: number | null;
  coveragePercentage: number | null;
}

export function createInitialWorkspaceConfig(): SyntheticGridConfig {
  return {
    ...DEFAULT_SYNTHETIC_GRID_CONFIG,
    aps: [],
    obstacles: [],
    observationHeight: DEFAULT_SYNTHETIC_GRID_CONFIG.observationHeight ?? 1
  };
}

export function createInitialConfigText(): string {
  return JSON.stringify(createInitialWorkspaceConfig(), null, 2);
}

export function computeScenarioState(params: {
  loading: boolean;
  accessPoints: AccessPoint[];
  obstacles: Obstacle[];
  needsSimulation: boolean;
  scenarioSaved: boolean;
}): ScenarioStateDescriptor {
  if (params.loading) {
    return { labelKey: 'heatmap.workspace.status.simulating', tone: 'active' };
  }
  if (!params.accessPoints.length && !params.obstacles.length) {
    return { labelKey: 'heatmap.workspace.status.empty', tone: 'neutral' };
  }
  if (params.needsSimulation) {
    return { labelKey: 'heatmap.workspace.status.dirty', tone: 'warning' };
  }
  if (params.scenarioSaved) {
    return { labelKey: 'heatmap.workspace.status.saved', tone: 'success' };
  }
  return { labelKey: 'heatmap.workspace.status.ready', tone: 'neutral' };
}

export function computeAnalysisSummary(points: HeatPoint[], threshold: number): HeatmapAnalysisSummary {
  if (!points.length) {
    return {
      averageSignal: null,
      bestSignal: null,
      coveragePercentage: null
    };
  }

  const sum = points.reduce((acc, point) => acc + point.value, 0);
  const averageSignal = Math.round((sum / points.length) * 10) / 10;
  const bestSignal = Math.max(...points.map((point) => point.value));
  const covered = points.filter((point) => point.value >= threshold).length;
  const coveragePercentage = Math.round((covered / points.length) * 100);

  return {
    averageSignal,
    bestSignal,
    coveragePercentage
  };
}

export function clampCellSize(value: number): number {
  const min = 5;
  const max = 100;
  if (Number.isNaN(value)) {
    return DEFAULT_SYNTHETIC_GRID_CONFIG.cell;
  }
  return Math.min(max, Math.max(min, value));
}

export function clampObservationHeight(value: number): number {
  const min = 0.1;
  const max = 10;
  if (Number.isNaN(value)) {
    return 1;
  }
  return Math.min(max, Math.max(min, value));
}

export function normalizeWorkspaceConfig(
  config: SyntheticGridConfig,
  fallbackObservationHeight: number
): SyntheticGridConfig {
  const clampedCell = clampCellSize(config.cell);
  const aps = config.aps?.map((ap) => ({ ...ap, height: ap.height ?? 7 })) ?? [];
  const obstacles = (config as any).obstacles?.map((obs: Obstacle) => ({ ...obs, height: obs.height ?? 3 })) ?? [];
  const observationHeight = clampObservationHeight(config.observationHeight ?? fallbackObservationHeight);
  return { ...config, aps, obstacles, cell: clampedCell, observationHeight };
}

export function randomizeWorkspaceConfig(
  config: SyntheticGridConfig,
  fallbackObservationHeight: number
): SyntheticGridConfig {
  const width = config.width;
  const height = config.height;
  const margin = 30;
  const randCoord = () => ({
    x: Math.random() * Math.max(1, width - margin * 2) + margin,
    y: Math.random() * Math.max(1, height - margin * 2) + margin
  });
  const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

  const aps = config.aps.map((ap) => {
    const coord = randCoord();
    const length = 160;
    const angle = Math.random() * Math.PI * 2;
    const endX = clamp(coord.x + Math.cos(angle) * length, margin, width - margin);
    const endY = clamp(coord.y + Math.sin(angle) * length, margin, height - margin);
    return { ...ap, x: coord.x, y: coord.y, x2: endX, y2: endY, height: ap.height ?? 7 };
  });

  const materials = ['concrete', 'drywall', 'glass', 'wood', 'metal'];
  const obstacles = (config as any).obstacles?.map((obs: Obstacle) => {
    const coord = randCoord();
    const length = 200 + Math.random() * 180;
    const angle = Math.random() * Math.PI * 2;
    const endX = clamp(coord.x + Math.cos(angle) * length, margin, width - margin);
    const endY = clamp(coord.y + Math.sin(angle) * length, margin, height - margin);
    const material = materials[Math.floor(Math.random() * materials.length)] ?? 'concrete';
    const loss = Math.round((obs.loss ?? 6) + (Math.random() * 6 - 3));
    const thickness = Math.max(10, Math.min(60, (obs.thickness ?? 30) + (Math.random() * 10 - 5)));
    const obstacleHeight = Math.max(2, Math.min(4.5, (obs.height ?? 3) + (Math.random() * 1.2 - 0.6)));
    return { ...obs, x: coord.x, y: coord.y, x2: endX, y2: endY, material, loss, thickness, height: obstacleHeight };
  }) ?? [];

  return {
    ...config,
    aps,
    obstacles,
    observationHeight: clampObservationHeight(config.observationHeight ?? fallbackObservationHeight)
  };
}

export function createAccessPointAt(
  x: number,
  y: number,
  imageWidth: number,
  template?: AntennaList
): AccessPoint {
  const defaultPower = antennaTemplatePower(template);
  const defaultSigma = antennaTemplateSigma(template);
  const length = Math.max(80, Math.min(imageWidth, template?.length || 160));
  return {
    x,
    y,
    x2: Math.min(imageWidth, x + length),
    y2: y,
    power: defaultPower,
    sigma: defaultSigma,
    height: 7
  };
}

export function createObstacleFromLine(
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  template?: ObstacleMaterial
): Obstacle {
  return {
    x: startX,
    y: startY,
    x2: endX,
    y2: endY,
    material: obstacleTemplateMaterial(template),
    loss: obstacleTemplateLoss(template),
    thickness: 30,
    height: 3
  };
}

function antennaTemplatePower(template?: AntennaList): number {
  if (!template) {
    return 55;
  }
  const omniGain = Math.max(
    template.terminationOmni?.freq24?.gain ?? 0,
    template.terminationOmni?.freq5?.gain ?? 0,
    template.terminationOmni?.freq6?.gain ?? 0
  );
  const byModel =
    template.model === AntennaModelEnum.OMNI ? 58 :
    template.model === AntennaModelEnum.ANT5 ? 62 :
    55;
  return Math.max(40, Math.min(85, Math.round(byModel + omniGain)));
}

function antennaTemplateSigma(template?: AntennaList): number {
  if (!template) {
    return 240;
  }
  const byType = template.type === TypeEnum.TYPE_C ? 210 : 260;
  const byLength = Math.round((template.length || 160) * 0.7);
  return Math.max(140, Math.min(360, Math.round((byType + byLength) / 2)));
}

function obstacleTemplateMaterial(template?: ObstacleMaterial): string {
  const name = template?.name?.trim().toLowerCase();
  if (!name) {
    return 'concrete';
  }
  if (name.includes('glass') || name.includes('vetro')) {
    return 'glass';
  }
  if (name.includes('wood') || name.includes('legno')) {
    return 'wood';
  }
  if (name.includes('metal') || name.includes('metallo') || name.includes('steel')) {
    return 'metal';
  }
  if (name.includes('drywall') || name.includes('cartongesso')) {
    return 'drywall';
  }
  return 'concrete';
}

function obstacleTemplateLoss(template?: ObstacleMaterial): number {
  if (!template) {
    return 8;
  }
  const values = [
    template.attenuations?.freq24 ?? 0,
    template.attenuations?.freq5 ?? 0,
    template.attenuations?.freq6 ?? 0
  ];
  const avg = values.reduce((sum, value) => sum + value, 0) / values.length;
  return Math.max(1, Math.round(avg || 8));
}
